import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { z } from 'zod';

import { createRESTContract } from '../../index';
import {
  BotLanguagesSchema,
  BotLanguagesUpdateSchema,
  ChatPluginConfigSchema,
  DataRetentionConfigSchema,
  DataRetentionEnabledSchema,
  DataRetentionModeSchema,
  DebugConfigSchema,
  PseudonymizationEnabledSchema,
  RestartPolicyWhitelistConfigSchema,
  StreamingResponseConfigSchema,
  TimezoneConfigSchema,
  UserDataMappingSchema,
  UserProfileFieldsConfigSchema,
  ValidationConfigSchema,
} from './bot.schema';

export const botContract = createRESTContract({
  updateBotLanguages: {
    method: 'PUT',
    path: '/bot/bot-languages',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: BotLanguagesUpdateSchema,
    response: z.void(),
  },
  getBotLanguages: {
    method: 'GET',
    path: '/bot/bot-languages',
    // * The Styling page renders one input per bot language, and its users
    // * hold the webclient permissions, not the application settings ones.
    anyPermissions: [
      PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS,
      PERMISSIONS.WEBCLIENT.WEBCLIENT_CONFIG_LIST,
    ],
    response: BotLanguagesSchema,
  },
  getDebugConfig: {
    method: 'GET',
    path: '/bot/debug-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: DebugConfigSchema,
  },
  updateDebugConfig: {
    method: 'POST',
    path: '/bot/debug-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    response: z.void(),
    body: DebugConfigSchema,
  },
  getValidationConfig: {
    method: 'GET',
    path: '/bot/validation-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: ValidationConfigSchema,
  },
  updateValidationConfig: {
    method: 'POST',
    path: '/bot/validation-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    response: z.void(),
    body: ValidationConfigSchema,
  },
  getUserDataMappingConfig: {
    method: 'GET',
    path: '/bot/user-data-mapping',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: UserDataMappingSchema,
  },
  updateUserDataMappingConfig: {
    method: 'POST',
    path: '/bot/user-data-mapping',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    response: z.void(),
    body: UserDataMappingSchema,
  },
  getTimezoneConfig: {
    method: 'GET',
    path: '/bot/timezone',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: TimezoneConfigSchema,
  },
  updateTimezoneConfig: {
    method: 'POST',
    path: '/bot/timezone',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    response: z.void(),
    body: TimezoneConfigSchema,
  },
  getStreamingResponseConfig: {
    method: 'GET',
    path: '/bot/streaming-response-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: StreamingResponseConfigSchema,
  },
  updateStreamingResponseConfig: {
    method: 'POST',
    path: '/bot/streaming-response-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    response: z.void(),
    body: StreamingResponseConfigSchema,
  },
  getRestartPolicyWhitelist: {
    method: 'GET',
    path: 'bot/restart-policy',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: RestartPolicyWhitelistConfigSchema,
  },
  updateRestartPolicyWhitelist: {
    method: 'POST',
    path: 'bot/restart-policy',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: RestartPolicyWhitelistConfigSchema,
    response: z.void(),
  },
  getUserProfileFieldsConfig: {
    method: 'GET',
    path: 'bot/user-profile-fields',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: UserProfileFieldsConfigSchema,
  },
  updateUserProfileFieldsConfig: {
    method: 'POST',
    path: 'bot/user-profile-fields',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: UserProfileFieldsConfigSchema,
    response: z.void(),
  },
  getChatPluginConfig: {
    method: 'GET',
    path: 'bot/chat-plugin',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: ChatPluginConfigSchema,
  },
  updateChatPluginConfig: {
    method: 'POST',
    path: 'bot/chat-plugin',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: ChatPluginConfigSchema,
    response: z.void(),
  },
  getDataRetentionConfig: {
    method: 'GET',
    path: 'bot/data-retention-config',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_LIST_SETTINGS],
    response: DataRetentionConfigSchema,
  },
  updateDataRetentionEnabled: {
    method: 'POST',
    path: 'bot/data-retention-config/enabled',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: DataRetentionEnabledSchema,
    response: z.void(),
  },
  updatePseudonymizationEnabled: {
    method: 'POST',
    path: 'bot/data-retention-config/pseudonymization',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: PseudonymizationEnabledSchema,
    response: z.void(),
  },
  updateDataRetentionMode: {
    method: 'POST',
    path: 'bot/data-retention-config/mode',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: DataRetentionModeSchema,
    response: z.void(),
  },
  runPseudonymization: {
    method: 'POST',
    path: 'bot/data-retention/run-pseudonymization',
    permissions: [PERMISSIONS.APPLICATION.APPLICATION_UPDATE_SETTINGS],
    body: z.undefined(),
    response: z.void(),
  },
});
